import { useQuery } from '@tanstack/react-query';
import { tokensApi } from '../lib/api';

/**
 * Saldo de Marbles do usuário LOGADO.
 *
 * Privacidade: `/tokens/balance` e `/tokens/history` filtram por `user["id"]`
 * no backend (tokens.py) e a view `pilot_token_balance` roda com
 * `security_invoker` + RLS "pilot vê as próprias" — nunca há dados de terceiros.
 */

export interface MarblesBalance {
  balance: number;
  last_update?: string;
}

export interface MarblesTransaction {
  id: string;
  pilot_id: string;
  amount: number;
  event_type: string;
  description: string;
  created_at: string;
}

const BALANCE_STALE = 30 * 1000;
const HISTORY_STALE = 60 * 1000;

/**
 * Saldo atual. Busca única e silenciosa (api.get já nasce `silent`),
 * cache compartilhado por todos os pontos de acesso (TopBar, perfil, settings).
 */
export function useMarblesBalance(enabled = true) {
  return useQuery<MarblesBalance>({
    queryKey: ['marbles', 'balance'],
    enabled,
    queryFn: async () => {
      const data = (await tokensApi.balance()) as any;
      return {
        balance: Number(data?.balance) || 0,
        last_update: data?.last_update,
      };
    },
    staleTime: BALANCE_STALE,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 1,
  });
}

/**
 * Extrato de transações paginado (mais recentes primeiro).
 * `amount` chega sinalizado: negativo = débito (taxa/compra), positivo = crédito.
 */
export function useMarblesHistory(limit = 10, offset = 0, enabled = true) {
  return useQuery<MarblesTransaction[]>({
    queryKey: ['marbles', 'history', limit, offset],
    enabled,
    queryFn: async () => {
      const data = (await tokensApi.history(limit, offset)) as any[];
      return Array.isArray(data) ? (data as MarblesTransaction[]) : [];
    },
    staleTime: HISTORY_STALE,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 1,
  });
}
